import type { ReactElement } from "react";
import { useId } from "react";

import { FormFieldLabel } from "@core/components/FormFieldLabel";
import { SupportingText } from "@core/components/SupportingText";
import type { SafeHtmlAttributes } from "@core/types/SafeHtmlAttributes";
import classnames from "classnames";

import type { RadioContainer, RadioContainerProps } from "./components/RadioContainer";

import { RadioContainerGroupContext } from "./RadioContainerGroupContext";

import styles from "./RadioContainerGroup.module.scss";

type RadioContainerChild = ReactElement<RadioContainerProps, typeof RadioContainer> | false | null | undefined;

export type RadioContainerGroupProps = {
  name?: string;
  label?: string;
  helperText?: string;
  errorText?: string;
  isError?: boolean;
  orientation?: "vertical" | "horizontal";
  children: RadioContainerChild | RadioContainerChild[];
} & SafeHtmlAttributes<HTMLFieldSetElement>;

export const RadioContainerGroup = ({
  name,
  label,
  helperText,
  errorText,
  isError = false,
  orientation = "vertical",
  children,
  "aria-describedby": ariaDescribedBy,
  ...rest
}: RadioContainerGroupProps) => {
  const supportingTextId = useId();
  const labelId = useId();
  const showHelperText = !isError && helperText;
  const showErrorText = isError && errorText;
  const resolvedAriaDescribedBy =
    [ariaDescribedBy, showHelperText || showErrorText ? supportingTextId : undefined]
      .flatMap((value) => value?.split(/\s+/) ?? [])
      .filter(Boolean)
      .join(" ") || undefined;

  return (
    <fieldset
      {...rest}
      className={styles.group}
      role="radiogroup"
      aria-labelledby={label ? labelId : undefined}
      aria-describedby={resolvedAriaDescribedBy}
      aria-invalid={isError || undefined}
    >
      {label && (
        <legend id={labelId} className={styles.legend}>
          <FormFieldLabel label={label} />
        </legend>
      )}

      <RadioContainerGroupContext.Provider value={{ isError, groupName: name }}>
        <div className={classnames(styles.list, styles[orientation])}>{children}</div>
      </RadioContainerGroupContext.Provider>

      {showHelperText && (
        <SupportingText id={supportingTextId} variant="info">
          {helperText}
        </SupportingText>
      )}

      {showErrorText && (
        <SupportingText id={supportingTextId} variant="error" role="alert">
          {errorText}
        </SupportingText>
      )}
    </fieldset>
  );
};
